import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RequestError } from '../../../interfaces/error.interface';
import { createEntityAdapter } from '../../../helpers/create-entity-adapter';
import { Reception } from '../../../interfaces/reception.interface';
import { GetReceptionsResponse } from '../interfaces/get-receptions';
import { GetReceptionOrdersResponse } from '../interfaces/get-reception-orders';
import { ReceptionOrder } from '../../../interfaces/receptionOrder.interface';
import { GetReceptionContainersResponse } from '../interfaces/get-reception-containers';
import { ReceptionContainer } from '../../../interfaces/receptionCotnainer.interface';

export interface ReceptionState {
  receptionOrders: {
    ids: string[];
    entities: Record<string, ReceptionOrder>;
  };
  receptionOrder: ReceptionOrder | null;
  receptions: {
    ids: string[];
    entities: Record<string, Reception>;
  };
  reception: Reception | null;
  receptionContainers: {
    ids: string[];
    entities: Record<string, ReceptionContainer>;
  };
  page: number;
  size: number;
  total: number;
  noMore: boolean;
  errorCode: number | null;
}

const initialState: ReceptionState = {
  receptionOrders: {
    ids: [],
    entities: {},
  },
  receptionOrder: null,
  receptions: {
    ids: [],
    entities: {},
  },
  reception: null,
  receptionContainers: {
    ids: [],
    entities: {},
  },
  page: 1,
  size: 100,
  total: 0,
  noMore: false,
  errorCode: null,
};

const receptionOrderAdapter = createEntityAdapter<ReceptionOrder>((reception: ReceptionOrder) => reception.id);
const receptionAdapter = createEntityAdapter<Reception>((reception: Reception) => reception.id);
const receptionContainerAdapter = createEntityAdapter<ReceptionContainer>((reception: ReceptionContainer) => reception.id);

const receptionSlice = createSlice({
  name: 'reception',
  initialState,
  reducers: {
    getReceptionsInitApiAction: (state) => {
      state.errorCode = null;
    },
    getReceptionsSuccessApiAction: (
      state,
      action: PayloadAction<GetReceptionsResponse>
    ) => {
      receptionAdapter.addMany(action.payload.receptions, state.receptions);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.receptions.length < state.size;
    },
    getReceptionsFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getReceptionContainerInitApiAction: (state) => {
      state.errorCode = null;
    },
    getReceptionContainerSuccessApiAction: () => {
    },
    getReceptionContainerFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getReceptionOrdersInitApiAction: (state) => {
      state.errorCode = null;
    },
    getReceptionOrdersSuccessApiAction: (
      state,
      action: PayloadAction<GetReceptionOrdersResponse>
    ) => {
      receptionOrderAdapter.addMany(action.payload.reception_orders, state.receptionOrders);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.reception_orders.length < state.size;
    },
    getReceptionOrdersFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    getReceptionContainersInitApiAction: (state) => {
      state.errorCode = null;
    },
    getReceptionContainersSuccessApiAction: (
      state,
      action: PayloadAction<GetReceptionContainersResponse>
    ) => {
      receptionContainerAdapter.addMany(action.payload.origin_containers, state.receptionContainers);
      state.total = action.payload.pagination.total_elements;
      state.page = action.payload.pagination.page;
      state.size = action.payload.pagination.size;
      state.noMore = action.payload.origin_containers.length < state.size;
    },
    getReceptionContainersFailureApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postReceptionInitApiAction: (state) => {
      state.errorCode = null;
    },
    postReceptionSuccessApiAction: () => {},
    postReceptionFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    postReceptionOrderInitApiAction: (state) => {
      state.errorCode = null;
    },
    postReceptionOrderSuccessApiAction: () => {},
    postReceptionOrderFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putReceptionInitApiAction: (state) => {
      state.errorCode = null;
    },
    putReceptionSuccessApiAction: () => {},
    putReceptionFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putReceptionContainerInitApiAction: (state) => {
      state.errorCode = null;
    },
    putReceptionContainerSuccessApiAction: () => {},
    putReceptionContainerFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    putReceptionOrderInitApiAction: (state) => {
      state.errorCode = null;
    },
    putReceptionOrderSuccessApiAction: () => {},
    putReceptionOrderFailuireApiAction: (state, action: PayloadAction<RequestError>) => {
      state.errorCode = action.payload.errorCode;
    },
    deleteReceptionInitApiAction: () => {},
    deleteReceptionSuccessApiAction: () => {},
    deleteReceptionFailuireApiAction: () => {},
    deleteReceptionOrderInitApiAction: () => {},
    deleteReceptionOrderSuccessApiAction: () => {},
    deleteReceptionOrderFailuireApiAction: () => {},
    setReceptionAction: (state, action: PayloadAction<Reception>) => {
      state.reception = action.payload;
    },
    setReceptionOrderAction: (state, action: PayloadAction<ReceptionOrder>) => {
      state.receptionOrder = action.payload;
    },
    increasePageAction: (state) => {
      state.page += 1;
    },
    resetPageAction: (state) => {
      state.page = 1;
    },
    receptionClearAction: (state) => {
      state.reception = null;
      state.receptionOrder = null;
      state.errorCode = null;
    },
  },
});

export const {
  getReceptionsInitApiAction,
  getReceptionsSuccessApiAction,
  getReceptionsFailureApiAction,
  getReceptionOrdersInitApiAction,
  getReceptionOrdersSuccessApiAction,
  getReceptionOrdersFailureApiAction,
  getReceptionContainersInitApiAction,
  getReceptionContainersSuccessApiAction,
  getReceptionContainersFailureApiAction,
  postReceptionInitApiAction,
  postReceptionSuccessApiAction,
  postReceptionFailuireApiAction,
  postReceptionOrderInitApiAction,
  postReceptionOrderSuccessApiAction,
  postReceptionOrderFailuireApiAction,
  putReceptionInitApiAction,
  putReceptionSuccessApiAction,
  putReceptionFailuireApiAction,
  putReceptionOrderInitApiAction,
  putReceptionOrderSuccessApiAction,
  putReceptionOrderFailuireApiAction,
  deleteReceptionInitApiAction,
  deleteReceptionSuccessApiAction,
  deleteReceptionFailuireApiAction,
  deleteReceptionOrderInitApiAction,
  deleteReceptionOrderSuccessApiAction,
  deleteReceptionOrderFailuireApiAction,
  putReceptionContainerInitApiAction,
  putReceptionContainerSuccessApiAction,
  putReceptionContainerFailuireApiAction,
  getReceptionContainerInitApiAction,
  getReceptionContainerSuccessApiAction,
  getReceptionContainerFailureApiAction,
  setReceptionAction,
  setReceptionOrderAction,
  increasePageAction,
  resetPageAction,
  receptionClearAction,
} = receptionSlice.actions;

export default receptionSlice.reducer;
